// src/utils/auth.ts

/**
 * Get the authentication token from either localStorage or sessionStorage.
 * Automatically trims spaces and returns an empty string if not found.
 */
import { jwtDecode } from "jwt-decode";
export const getAuthToken = (): string => {
  try {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      "";
    return token.trim();
  } catch (error) {
    console.error("Error fetching auth token:", error);
    return "";
  }
};

/**
 * Save token to preferred storage (localStorage or sessionStorage)
 * @param token JWT token string
 * @param rememberUser If true → stores in localStorage; else → sessionStorage
 */
export const setAuthToken = (token: string, rememberUser = false): void => {
  try {
    if (rememberUser) {
      localStorage.setItem("token", token);
      sessionStorage.removeItem("token");
    } else {
      sessionStorage.setItem("token", token);
      localStorage.removeItem("token");
    }
  } catch (error) {
    console.error("Error saving auth token:", error);
  }
};

/**
 * Remove token from both storages.
 */
export const clearAuthToken = (): void => {
  try {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
  } catch (error) {
    console.error("Error clearing auth token:", error);
  }
};

/**
 * Common headers including Authorization (Bearer).
 * Useful for Axios, fetch, etc.
 */
export const getAuthHeaders = (): HeadersInit => {
  const authToken = getAuthToken();
  return {
    Authorization: `Bearer ${authToken}`,
    AuthorizationData: authToken,
    "Content-Type": "application/json",
    Accept: "*/*",
  };
};

export const getUserIdFromToken = (): number | null => {
  const decoded: any = getDecodedUser();

  if (!decoded) return null;

  const userId =
    decoded.userId ||
    decoded.id ||
    decoded.nameid ||
    decoded.sub ||
    decoded.UserId ||
    null;

  return userId ? Number(userId) : null;
};


/**
 * Safely decodes a JWT token and returns its payload as an object.
 * Returns `null` if the token is invalid or cannot be decoded.
 */
export const decodeToken = <T = any>(token: string): T | null => {
  if (!token) return null;

  try {
    const decoded: any = jwtDecode(token);

    return {
      ...decoded,
      role:
        decoded.role ||
        decoded.roleName ||
        decoded[
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
        ],
      userId:
        decoded.userId ||
        decoded.id ||
        decoded.nameid ||
        decoded.sub ||
        decoded[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
        ]
    } as T;
  } catch (err) {
    console.error("JWT decode error:", err);
    return null;
  }
};



/**
 * Helper to directly get the decoded user payload from token.
 */
export const getDecodedUser = <T = any>(): T | null => {
  const token = getAuthToken();
  return token ? decodeToken<T>(token) : null;
};
